import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

if ("serviceWorker" in navigator) {
    // Auto-update: check for new app versions on boot, every 30 min, and when
    // the driver returns to the tab — new versions activate by themselves.
    // updateViaCache:"none" is the critical bit: without it the browser may
    // serve a CACHED sw.js for up to 24h, so a fresh deploy never reaches an
    // installed PWA until the next day (drivers were reinstalling to force
    // updates — never again).
    navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).then((reg) => {
        reg.update(); // boot check — a deploy that landed while the app was closed applies now
        setInterval(() => reg.update(), 15 * 60 * 1000);
        document.addEventListener("visibilitychange", () => {
            if (!document.hidden) reg.update();
        });
    }).catch(() => {});

    // Seamless updates: never flash an error screen, never tear down a live
    // route. The locked corridor is persisted continuously, so a reload
    // restores it — but while a driver is actively navigating, defer the swap
    // until the app is backgrounded (15-min cap), with a 2-minute cooloff so
    // a flip-flopping worker can never loop reloads.
    const navActive = () => {
        try {
            return !!window.__pcf?.navActive?.();
        } catch {
            return false;
        }
    };
    const reloadOnce = () => {
        const last = Number(sessionStorage.getItem("pcf_update_reload") || 0);
        if (Date.now() - last < 120000) return; // cooloff — never loop
        sessionStorage.setItem("pcf_update_reload", String(Date.now()));
        window.location.reload();
    };
    const onUpdateReady = () => {
        if (navActive() && document.visibilityState === "visible") {
            const retry = () => {
                if (document.visibilityState === "hidden" || !navActive()) reloadOnce();
            };
            document.addEventListener("visibilitychange", retry);
            setTimeout(() => {
                document.removeEventListener("visibilitychange", retry);
                reloadOnce(); // cap — swap even if still rolling
            }, 15 * 60 * 1000);
            return;
        }
        reloadOnce();
    };
    const hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (hadController) onUpdateReady();
    });

    // A lazy chunk that 404s after a deploy must never reach the driver as a
    // red error screen — swap into the fresh build instead.
    const isChunkError = (msg) =>
        /Loading chunk|ChunkLoadError|dynamically imported module|Importing a module script failed/i.test(
            String(msg || ""),
        );
    window.addEventListener("error", (e) => {
        if (isChunkError(e.message)) onUpdateReady();
    });
    window.addEventListener("unhandledrejection", (e) => {
        if (isChunkError(e.reason?.message || e.reason)) onUpdateReady();
    });
}
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
